import { useEffect, useState } from 'react';
import { SupplierTender } from '../../../../types/procurement-types';
import moment from 'moment';
import 'moment/locale/pt';
import 'moment/locale/tet';
import { requestTenderAccess } from '../../../../services/procurementService';
import { useTranslation } from 'react-i18next';

interface ProcurementCardProps {
  procurement: SupplierTender;
  onRequestAccess: (serviceInformationId: string) => void;
}

export const ProcurementCard: React.FC<ProcurementCardProps> = ({
  procurement,
  onRequestAccess: _onRequestAccess,
}) => {
  const [isFavorite, setIsFavorite] = useState(procurement.isFavorite);
  const supplierId = localStorage.getItem('supplierId') || '';
  const { t, i18n } = useTranslation();

  // Set moment.js locale whenever the language changes
  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  const handleFavoriteItem = async () => {
    const previousIsFavorite = isFavorite;

    try {
      setIsFavorite((prev) => !prev);
      await requestTenderAccess({
        serviceInformationId: procurement.serviceInformationId,
        supplierId,
        isFavorite: !previousIsFavorite,
      });
    } catch (error) {
      console.error(t('failedToRequestTenderAccess', 'Failed to request tender access:'), error);
      setIsFavorite(previousIsFavorite);
    }
  };

  const formattedCreatedDate = moment(procurement.createdDate).fromNow();

  return (
    <div className="cursor-pointer rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <span className="rounded-[30px] border border-[#E5E5E5] px-2 py-1 text-xs text-[#8D8D8D]">
          {formattedCreatedDate}
        </span>

        <button className="cursor-pointer" onClick={handleFavoriteItem}>
          <img
            src={`${process.env.PUBLIC_URL}/assets/icons/outline/${
              isFavorite ? 'favourite-filled.svg' : 'favourite.svg'
            }`}
            alt="favorite-icon"
          />
        </button>
      </div>

      <h3 className="mb-3 line-clamp-2 min-h-11 text-base font-medium text-[#161616]">
        {procurement.title}
      </h3>

      <p className="mb-6 line-clamp-3 min-h-[60px] text-sm text-[#667085]">
        {procurement.description}
      </p>

      <button
        className={`flex w-full justify-center rounded-lg border px-3 py-2.5 text-base font-medium ${
          procurement.requestAccess && procurement.approveAccess
            ? 'border-[#24A148] text-[#24A148]'
            : procurement.requestAccess && !procurement.approveAccess
              ? 'cursor-not-allowed border-gray-200 text-gray-400'
              : 'border-[#E5E5E5] text-[#101828]'
        }`}
        onClick={() => _onRequestAccess(procurement.serviceInformationId)}
        disabled={!!procurement.requestAccess && !procurement.approveAccess}
      >
        {procurement.requestAccess && procurement.approveAccess
          ? t('viewDetails', 'View Details')
          : procurement.requestAccess && !procurement.approveAccess
            ? t('_pending', 'Pending')
            : t('requestToView', 'Request to View')}
      </button>
    </div>
  );
};
